import { lockScroll, unlockScroll } from '@theme/utilities';

const MIN_SCALE = 1;
const CLICK_SCALE = 1.5;
const MAX_SCALE = 5;
const SWIPE_DISTANCE = 56;
const DOUBLE_TAP_DELAY = 320;

/** Full image gallery opened from a component product in Build Your Look. */
class LookGallery extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #thumbnails = [];

  /** @type {Map<number, { x: number, y: number }>} */
  #pointers = new Map();

  #index = 0;
  #scale = MIN_SCALE;
  #translate = { x: 0, y: 0 };
  #gestureStart = { x: 0, y: 0, distance: 0, scale: MIN_SCALE };
  #moved = false;
  #lastTap = { time: 0, x: 0, y: 0 };

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLButtonElement | null} */
  #selectButton = null;

  connectedCallback() {
    const trigger = this.querySelector('[data-look-gallery-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) return;
    this.#abort = new AbortController();
    trigger.addEventListener('click', () => {
      if (!this.#dialog) this.#initialize();
      if (this.#dialog) this.#open(trigger, this.#dialog);
    }, { signal: this.#abort.signal });
  }

  // Keep closed galleries inert: no image requests, thumbnail construction or
  // pointer listeners until the customer opens this particular piece.
  #initialize() {
    const template = this.querySelector('[data-look-gallery-template]');
    if (!(template instanceof HTMLTemplateElement)) return;
    this.append(template.content.cloneNode(true));
    const trigger = this.querySelector('[data-look-gallery-trigger]');
    const dialog = this.querySelector('[data-look-gallery-dialog]');
    const close = this.querySelector('[data-look-gallery-close]');
    const previous = this.querySelector('[data-look-gallery-previous]');
    const next = this.querySelector('[data-look-gallery-next]');
    const select = this.querySelector('[data-look-gallery-select]');

    if (!(trigger instanceof HTMLButtonElement) || !(dialog instanceof HTMLDialogElement)) return;

    this.#dialog = dialog;
    this.#selectButton = select instanceof HTMLButtonElement ? select : null;
    this.#slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-look-gallery-slide]')]);
    if (this.#slides.length === 0) return;

    this.#buildThumbnails();
    this.#setNavigationVisibility();

    const { signal } = this.#abort;
    close?.addEventListener('click', () => this.#close(trigger, dialog), { signal });
    previous?.addEventListener('click', () => this.#select(this.#index - 1, false), { signal });
    next?.addEventListener('click', () => this.#select(this.#index + 1, false), { signal });
    this.#selectButton?.addEventListener('click', () => this.#selectVariant(trigger, dialog), { signal });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close(trigger, dialog);
    }, { signal });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.#close(trigger, dialog);
    }, { signal });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.#select(this.#index - 1);
      if (event.key === 'ArrowRight') this.#select(this.#index + 1);
    }, { signal });

    for (const viewport of this.querySelectorAll('[data-look-gallery-viewport]')) {
      if (!(viewport instanceof HTMLButtonElement)) continue;

      viewport.addEventListener('click', (event) => event.preventDefault(), { signal });
      viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.#toggleZoom();
      }, { signal });
      viewport.addEventListener('pointerdown', (event) => this.#pointerDown(event), { signal });
      viewport.addEventListener('pointermove', (event) => this.#pointerMove(event), { signal });
      viewport.addEventListener('pointerup', (event) => this.#pointerUp(event), { signal });
      viewport.addEventListener('pointercancel', (event) => this.#pointerUp(event), { signal });
    }

    // A removed look item fades to half opacity. Moving only the top-layer dialog
    // outside that card keeps the trigger faded while the gallery remains clear.
    document.body.append(dialog);
  }

  disconnectedCallback() {
    this.#abort.abort();
    const dialog = this.#dialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) unlockScroll(dialog);
    dialog?.remove();
    this.#dialog = null;
  }

  /** @param {HTMLButtonElement} trigger @param {HTMLDialogElement} dialog */
  #open(trigger, dialog) {
    const triggerImage = trigger.querySelector('img');
    const selected = this.#slides.findIndex((slide) => {
      const mediaUrl = slide.dataset.mediaUrl;
      return mediaUrl && triggerImage instanceof HTMLImageElement && sameImage(mediaUrl, triggerImage.currentSrc || triggerImage.src);
    });

    this.#select(selected >= 0 ? selected : 0, false);
    lockScroll(dialog);
    dialog.showModal();
    this.#load(this.#index);
    dialog.querySelector('[data-look-gallery-close]')?.focus();
  }

  /** @param {HTMLButtonElement} trigger @param {HTMLDialogElement} dialog */
  #close(trigger, dialog) {
    if (!dialog.open) return;
    this.#resetZoom();
    unlockScroll(dialog);
    dialog.close();
    trigger.focus();
  }

  #buildThumbnails() {
    const container = this.querySelector('[data-look-gallery-thumbnails]');
    if (!(container instanceof HTMLElement)) return;

    this.#thumbnails = this.#slides.map((slide, index) => {
      const button = document.createElement('button');
      const image = document.createElement('img');

      button.type = 'button';
      button.className = 'button button-unstyled look-gallery__thumbnail';
      button.setAttribute('role', 'listitem');
      button.setAttribute('aria-label', slide.dataset.thumbnailLabel || `${index + 1}`);
      button.setAttribute('aria-selected', String(index === 0));
      button.addEventListener('click', () => this.#select(index), { signal: this.#abort.signal });

      image.src = slide.dataset.thumbnailUrl || '';
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      button.append(image);
      container.append(button);
      return button;
    });
  }

  #setNavigationVisibility() {
    const single = this.#slides.length <= 1;
    for (const button of this.querySelectorAll('[data-look-gallery-previous], [data-look-gallery-next]')) {
      if (button instanceof HTMLButtonElement) button.hidden = single;
    }
  }

  /** @param {number} index @param {boolean} [focusThumbnail] */
  #select(index, focusThumbnail = true) {
    const count = this.#slides.length;
    if (count === 0) return;

    this.#index = (index + count) % count;
    this.#resetZoom();

    this.#slides.forEach((slide, current) => slide.setAttribute('aria-hidden', String(current !== this.#index)));
    this.#thumbnails.forEach((thumbnail, current) => {
      thumbnail.setAttribute('aria-selected', String(current === this.#index));
    });

    const selectedThumbnail = this.#thumbnails[this.#index];
    selectedThumbnail?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    if (focusThumbnail && matchMedia('(min-width: 750px)').matches) selectedThumbnail?.focus({ preventScroll: true });

    const counter = this.#dialog?.querySelector('[data-look-gallery-counter]');
    if (counter instanceof HTMLOutputElement) counter.value = `${this.#index + 1} / ${count}`;
    else if (counter instanceof HTMLElement) counter.textContent = `${this.#index + 1} / ${count}`;

    this.#load(this.#index);
    this.#updateSelectButton();
  }

  /**
   * The card controls that would pick the variant of the photo in view, and whether they
   * are already picked. Read from the card each time rather than from the server render,
   * because the card changes colour in place without re-rendering anything.
   *
   * @returns {{ controls: { control: HTMLInputElement | HTMLSelectElement, value: string }[], current: boolean } | null}
   */
  #selectTarget() {
    const slide = this.#slides[this.#index];
    if (!slide?.dataset.selectVariant) return null;

    const gift = this.closest('[data-look-gift]');
    if (gift) {
      const radio = gift.querySelector(
        `input[type="radio"][data-look-gift-variant][value="${CSS.escape(slide.dataset.selectVariant)}"]`
      );
      if (!(radio instanceof HTMLInputElement)) return null;
      return { controls: [{ control: radio, value: radio.value }], current: radio.checked };
    }

    const options = this.closest('[data-look-item]')?.querySelector('.look-item__options');
    if (!options) return null;

    /** @type {[number, string][]} */
    let pairs;
    try {
      pairs = JSON.parse(slide.dataset.selectOptions || '[]');
    } catch {
      return null;
    }

    const controls = [];
    for (const [index, value] of pairs) {
      const radio = [...options.querySelectorAll(`input[data-option-index="${index}"]`)].find(
        (input) => input instanceof HTMLInputElement && input.value === value
      );
      const select = options.querySelector(`select[data-option-index="${index}"]`);

      if (radio instanceof HTMLInputElement) controls.push({ control: radio, value });
      else if (select instanceof HTMLSelectElement) controls.push({ control: select, value });
      else return null;
    }

    if (controls.length === 0) return null;

    const current = controls.every(({ control, value }) =>
      control instanceof HTMLInputElement ? control.checked : control.value === value
    );
    return { controls, current };
  }

  #updateSelectButton() {
    const button = this.#selectButton;
    if (!button) return;

    const target = this.#selectTarget();
    button.hidden = !target;
    if (!target) return;

    const slide = this.#slides[this.#index];
    button.textContent = (target.current ? slide?.dataset.selectedText : slide?.dataset.selectText) ?? '';
    button.toggleAttribute('data-current', target.current);
    button.setAttribute('aria-disabled', String(target.current));
  }

  /**
   * Picks the photo's variant on the card, then closes the gallery. The change events
   * bubble to the look builder, which reprices the card and swaps its photo as if the
   * shopper had pressed the swatch herself.
   *
   * @param {HTMLButtonElement} trigger @param {HTMLDialogElement} dialog
   */
  #selectVariant(trigger, dialog) {
    const target = this.#selectTarget();
    if (!target || target.current) return;

    this.#close(trigger, dialog);

    for (const { control, value } of target.controls) {
      if (control instanceof HTMLInputElement) {
        if (control.checked) continue;
        control.checked = true;
      } else {
        if (control.value === value) continue;
        control.value = value;
      }
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {number} index */
  #load(index) {
    const image = this.#slides[index]?.querySelector('img[data-full-src]');
    if (!(image instanceof HTMLImageElement) || image.dataset.fullLoaded === 'true') return;

    const source = image.dataset.fullSrc;
    if (!source) return;
    image.src = source;
    image.removeAttribute('srcset');
    image.dataset.fullLoaded = 'true';
  }

  /** @param {PointerEvent} event */
  #pointerDown(event) {
    const viewport = event.currentTarget;
    if (!(viewport instanceof HTMLButtonElement)) return;

    viewport.setPointerCapture(event.pointerId);
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.#moved = false;

    if (this.#pointers.size === 1) {
      this.#gestureStart = { x: event.clientX, y: event.clientY, distance: 0, scale: this.#scale };
    } else if (this.#pointers.size === 2) {
      this.#gestureStart.distance = pointerDistance(this.#pointers);
      this.#gestureStart.scale = this.#scale;
    }
  }

  /** @param {PointerEvent} event */
  #pointerMove(event) {
    if (!this.#pointers.has(event.pointerId)) return;
    const previous = this.#pointers.get(event.pointerId);
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const viewport = event.currentTarget;
    if (!(viewport instanceof HTMLButtonElement)) return;

    if (this.#pointers.size === 2) {
      const distance = pointerDistance(this.#pointers);
      if (this.#gestureStart.distance > 0) {
        this.#scale = clamp(this.#gestureStart.scale * (distance / this.#gestureStart.distance), MIN_SCALE, MAX_SCALE);
        this.#moved = true;
        this.#paintTransform(viewport);
      }
      return;
    }

    const dx = event.clientX - this.#gestureStart.x;
    const dy = event.clientY - this.#gestureStart.y;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) this.#moved = true;

    if (this.#scale > MIN_SCALE) {
      this.#translate.x += event.clientX - (previous?.x ?? event.clientX);
      this.#translate.y += event.clientY - (previous?.y ?? event.clientY);
      this.#constrainPan(viewport);
      this.#paintTransform(viewport);
    }
  }

  /** @param {PointerEvent} event */
  #pointerUp(event) {
    const viewport = event.currentTarget;
    if (!(viewport instanceof HTMLButtonElement) || !this.#pointers.has(event.pointerId)) return;

    const dx = event.clientX - this.#gestureStart.x;
    const dy = event.clientY - this.#gestureStart.y;
    const wasMultiTouch = this.#pointers.size > 1;
    this.#pointers.delete(event.pointerId);

    if (this.#scale <= MIN_SCALE && !wasMultiTouch && Math.abs(dx) >= SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
      this.#select(this.#index + (dx < 0 ? 1 : -1), false);
      return;
    }

    if (wasMultiTouch || this.#moved) return;

    if (event.pointerType === 'mouse') {
      this.#toggleZoom();
      return;
    }

    const now = performance.now();
    const closeEnough = Math.hypot(event.clientX - this.#lastTap.x, event.clientY - this.#lastTap.y) < 32;
    if (now - this.#lastTap.time <= DOUBLE_TAP_DELAY && closeEnough) {
      this.#toggleZoom();
      this.#lastTap.time = 0;
    } else {
      this.#lastTap = { time: now, x: event.clientX, y: event.clientY };
    }
  }

  #toggleZoom() {
    this.#scale = this.#scale > MIN_SCALE ? MIN_SCALE : CLICK_SCALE;
    this.#translate = { x: 0, y: 0 };
    const viewport = this.#activeViewport;
    if (viewport) this.#paintTransform(viewport);
  }

  #resetZoom() {
    this.#scale = MIN_SCALE;
    this.#translate = { x: 0, y: 0 };
    this.#pointers.clear();
    for (const slide of this.#slides) {
      const viewport = slide.querySelector('[data-look-gallery-viewport]');
      if (viewport instanceof HTMLButtonElement) this.#paintTransform(viewport);
    }
  }

  /** @param {HTMLButtonElement} viewport */
  #constrainPan(viewport) {
    const image = viewport.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    const maxX = Math.max(0, (image.offsetWidth * this.#scale - viewport.clientWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * this.#scale - viewport.clientHeight) / 2);
    this.#translate.x = clamp(this.#translate.x, -maxX, maxX);
    this.#translate.y = clamp(this.#translate.y, -maxY, maxY);
  }

  /** @param {HTMLButtonElement} viewport */
  #paintTransform(viewport) {
    const image = viewport.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    viewport.dataset.zoomed = String(this.#scale > MIN_SCALE);
    viewport.dataset.dragging = String(this.#pointers.size > 0 && this.#moved);
    image.style.transform = `translate3d(${this.#translate.x}px, ${this.#translate.y}px, 0) scale(${this.#scale})`;
  }

  /** @returns {HTMLButtonElement | null} */
  get #activeViewport() {
    const viewport = this.#slides[this.#index]?.querySelector('[data-look-gallery-viewport]');
    return viewport instanceof HTMLButtonElement ? viewport : null;
  }
}

/** @param {string} first @param {string} second */
function sameImage(first, second) {
  try {
    const firstUrl = new URL(first, window.location.origin);
    const secondUrl = new URL(second, window.location.origin);
    return firstUrl.hostname === secondUrl.hostname && firstUrl.pathname === secondUrl.pathname;
  } catch {
    return first === second;
  }
}

/** @param {Map<number, { x: number, y: number }>} pointers */
function pointerDistance(pointers) {
  const [first, second] = [...pointers.values()];
  if (!first || !second) return 0;
  return Math.hypot(second.x - first.x, second.y - first.y);
}

/** @param {number} value @param {number} minimum @param {number} maximum */
function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

if (!customElements.get('look-gallery')) customElements.define('look-gallery', LookGallery);
